import { useGetList } from "ra-core";
import { Badge } from "@/components/ui/badge";
import type { Subscriber } from "../types";
import {
  subscriberHasMarketingConsent,
  subscriberStatusLabel,
} from "../subscribers/subscriberUtils";

/**
 * Marketing-email consent for one address, read from the mail list
 * (`subscribers`). Giving Felix an email for the HD poster subscribes it there
 * (the WhatsApp CTA discloses offers and updates), so the list row is the
 * consent record and an unsubscribe withdraws it. Renders nothing while
 * loading or when the viewer cannot read the list.
 */
export const ContactMarketingConsent = ({ email }: { email: string }) => {
  const { data, isPending, error } = useGetList<Subscriber>("subscribers", {
    filter: { email },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "created_at", order: "DESC" },
  });
  if (isPending || error) return null;

  const subscriber = data?.[0];
  if (!subscriber) {
    return (
      <span className="text-xs text-muted-foreground">
        Not on the mail list
      </span>
    );
  }

  const consented = subscriberHasMarketingConsent(subscriber.status);
  const since = consented
    ? subscriber.created_at
    : (subscriber.updated_at ?? subscriber.created_at);
  return (
    <div className="flex items-center gap-2">
      <Badge variant={consented ? "default" : "outline"}>
        {consented
          ? "Marketing consent"
          : subscriberStatusLabel(subscriber.status)}
      </Badge>
      <span className="text-xs text-muted-foreground">
        {consented ? "since" : "on"} {new Date(since).toLocaleDateString()}
        {subscriber.source ? ` · ${subscriber.source}` : ""}
      </span>
    </div>
  );
};
